import { computed, watch, type ComputedRef } from 'vue'

import { platformActionApi } from '../api/PlatformActionApi'
import { LoadActionsFlow } from '../flows/LoadActionsFlow'
import type { PlatformActionRequest } from '../models/platformAction.types'
import type { PlatformActionError } from '../models/platformActionError'
import { toFirst, toPageParams } from '../service/pageParams'
import { usePlatformActionStore, type PlatformActionFilters } from '../stores/platformActionStore'

import { useWorkspace } from '@/features/account'
import type { UiAsyncState } from '@/shared/ui'

interface ListFlow {
  execute: (
    workspaceId: number,
    query: { status: string; platform: string; page: number; pageSize: number },
  ) => Promise<void>
}

/** Internal injection seam: production callers use the no-argument contract. */
export interface UsePlatformActionsOptions {
  flow?: ListFlow
}

export interface UsePlatformActions {
  state: ComputedRef<UiAsyncState>
  error: ComputedRef<PlatformActionError | null>
  requests: ComputedRef<PlatformActionRequest[]>
  filters: ComputedRef<PlatformActionFilters>
  /** Zero-based row offset the shared `Pagination` primitive binds to. */
  first: ComputedRef<number>
  rows: ComputedRef<number>
  total: ComputedRef<number>
  pendingApprovalCount: ComputedRef<number>
  workspaceId: ComputedRef<number | null>
  setFilters: (next: PlatformActionFilters) => void
  setPagination: (change: { first: number; rows: number }) => void
  reload: () => Promise<void>
}

/** The list surface: filters, pagination and the four async states for the page's table. */
export function usePlatformActions(options: UsePlatformActionsOptions = {}): UsePlatformActions {
  const workspace = useWorkspace()
  const store = usePlatformActionStore()
  const flow = options.flow ?? new LoadActionsFlow({ api: platformActionApi, store })

  const workspaceId = computed(() => numericId(workspace.workspaceId.value))

  async function load(): Promise<void> {
    const id = workspaceId.value
    if (id === null) return
    await flow.execute(id, {
      status: store.filters.status,
      platform: store.filters.platform,
      page: store.page,
      pageSize: store.pageSize,
    })
  }

  // One watcher over every input the query is built from: a workspace switch, a filter change and a
  // page change all mean the same thing — re-fetch, with the store's key guard dropping stale ones.
  watch(
    () =>
      [
        workspaceId.value,
        store.filters.status,
        store.filters.platform,
        store.page,
        store.pageSize,
      ] as const,
    () => {
      void load()
    },
    { immediate: true },
  )

  return {
    state: computed(() => asyncState(store.status, store.requests.length)),
    error: computed(() => store.error),
    requests: computed(() => store.requests),
    filters: computed(() => store.filters),
    first: computed(() => toFirst(store.page, store.pageSize)),
    rows: computed(() => store.pageSize),
    total: computed(() => store.total),
    pendingApprovalCount: computed(() => store.pendingApprovalCount),
    workspaceId,
    setFilters: (next) => store.setFilters(next),
    setPagination: ({ first, rows }) => {
      const params = toPageParams(first, rows)
      if (params.page_size !== store.pageSize) store.setPageSize(params.page_size)
      else store.setPageNumber(params.page)
    },
    reload: load,
  }
}

function asyncState(status: string, count: number): UiAsyncState {
  if (status === 'loading' || status === 'idle') return 'loading'
  if (status === 'error') return 'error'
  return count === 0 ? 'empty' : 'ready'
}

function numericId(value: string | number | undefined): number | null {
  if (value === undefined || (typeof value === 'string' && value.trim() === '')) return null
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}
